import { Timestamp } from 'firebase/firestore';
import { formatDisplayName, splitName } from './name';

export const ROLES = [
  'employee',
  'rd_meca',
  'rd_elec',
  'rd_test_bench',
  'rd_manager',
  'dft',
  'supply_chain',
  'finance',
  'bizdev',
  'quality',
  'marketing',
  'project_manager',
  'direction',
  'admin',
] as const;

export type Role = (typeof ROLES)[number];

export const AUTO_ASSIGNED_ROLES: Role[] = ['finance', 'rd_manager', 'direction', 'quality'];

/**
 * Display-ordered catalogue of the 13 global roles.
 *
 * Source of truth for the admin Rôles tab (EditRolesDialog), the Sidebar's
 * dev role-switcher, and any other consumer that needs to enumerate or render
 * roles. The order is intentional — it groups R&D roles together, then
 * cross-functional roles, then governance — and is what admins see in the
 * chip list when assigning roles.
 */
export const ROLE_CATALOG: readonly Role[] = ROLES.filter((r) => r !== 'rd_test_bench');

/**
 * Human-readable labels for each role, in English and French.
 *
 * Kept here (not in the i18n JSON) so the shared-types package is the single
 * source of truth for the role catalogue. UI consumers pick the current
 * language via `i18n.language` and fall back to `en`. Adding a new role
 * requires updating this map, which guarantees coverage at compile time via
 * `Record<Role, ...>`.
 */
export const ROLE_LABELS: Record<Role, { en: string; fr: string }> = {
  employee: { en: 'Employee', fr: 'Employé' },
  rd_meca: { en: 'R&D Mechanical', fr: 'R&D Mécanique' },
  rd_elec: { en: 'R&D Electronics', fr: 'R&D Électronique' },
  rd_test_bench: { en: 'R&D Test Bench', fr: 'R&D Banc de test' },
  rd_manager: { en: 'R&D Manager', fr: 'Responsable R&D' },
  dft: { en: 'DFT', fr: 'DFT' },
  supply_chain: { en: 'Supply Chain', fr: 'Supply Chain' },
  finance: { en: 'Finance', fr: 'Finance' },
  bizdev: { en: 'Business Development', fr: 'Business Development' },
  quality: { en: 'Quality', fr: 'Qualité' },
  marketing: { en: 'Marketing', fr: 'Marketing' },
  project_manager: { en: 'Project Manager', fr: 'Chef de projet' },
  direction: { en: 'Direction', fr: 'Direction' },
  admin: { en: 'Admin', fr: 'Administrateur' },
};

/** Returns the human-readable label for a role in the requested language. */
export function getRoleLabel(role: Role, lang: 'en' | 'fr' = 'en'): string {
  return ROLE_LABELS[role][lang];
}

export interface NotificationPrefs {
  inApp: boolean;
  push: boolean;
  email: boolean;
  dailyReminder: boolean;
  reminderTime: string;
}

export interface User {
  uid: string;
  email: string;
  displayName: string;
  /** Source of truth for the name; displayName is derived via formatDisplayName. Optional: legacy docs predate it. */
  firstName?: string;
  lastName?: string;
  /** Admin-controlled lifecycle flag. Read as `active !== false` (legacy docs lack it → treated active). Default true. */
  active?: boolean;
  photoURL?: string;
  jobTitle?: string;
  roles: Role[];
  departmentId: string;
  favorites: string[];
  hiddenProjects: string[];
  legalAcceptedAt?: Timestamp;
  notificationPrefs: NotificationPrefs;
  /** V1: manually set by admin/user to suppress reminders until this date. V2: auto-populated from Outlook calendar. */
  pausedReminderUntil?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * The document a brand-new Kronos account starts from.
 *
 * Shared because account creation has two doors that must not drift apart:
 * the offline `tools/users/seed-*` scripts, and kronos-api turning an admin's
 * invitation into a real account at first login (`actions/provisionUser.ts`).
 * Timestamps are left to the caller — each door stamps them its own way.
 */
export function buildUserDoc(m: {
  oid: string;
  displayName: string;
  email: string;
  departmentId: string;
}) {
  const { firstName, lastName } = splitName(m.displayName);
  return {
    uid: m.oid, email: m.email, firstName, lastName,
    displayName: formatDisplayName(firstName, lastName),
    active: true, photoURL: null, jobTitle: null,
    roles: ['employee'], departmentId: m.departmentId,
    favorites: [], hiddenProjects: [],
    notificationPrefs: { inApp: true, push: true, email: true, dailyReminder: true, reminderTime: '17:30' },
  };
}

/**
 * Une invitation en attente de première connexion (`userInvites/{id}`).
 *
 * Un compte ne peut pas être créé d'avance : `users/{oid}` est keyé par l'oid
 * Entra, inconnu tant que la personne ne s'est pas connectée une fois. L'admin
 * pose donc une invitation, que `kronos-api` transforme en compte au premier
 * jeton portant ce même e-mail — puis la supprime.
 */
export interface UserInvite {
  email: string;
  firstName: string;
  lastName: string;
  departmentId: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}
